/**
 * NOTA — `paragraph` en Typography
 *
 * MUI 9 removio ese prop. Los `{...({ paragraph: true } as object)}` de este archivo lo
 * siguen pasando, pero hoy se ignora: esos bloques perdieron su margin-bottom y ya no
 * renderizan como parrafo. Se conserva el comportamiento actual; reemplazarlo por
 * sx + component="p" cambia como se ve y va en su propio ticket.
 */
import { useState } from "react";

import { format, parseISO } from "date-fns";
import { Box, Button, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import DialogSupportComponent from "@vendor/examples/Navbars/DashboardNavbar/component/dialogSupportComponent";
import {
  contentSupportVacancy,
  titleSupportVacancy,
} from "@vendor/examples/Navbars/DashboardNavbar/component/messageDialog";

import { PostulateCandidateDialog } from "./PostulateCandidateDialog";
import { useSendVacancyMessage } from "../hooks/useSendVacancyMessage";
import { PRIORITY_LABEL_KEYS } from "../constants";

import type { Vacancy } from "../api/vacancyApi";

export interface JobCardProps {
  vacancy: Vacancy;
  thisLoading: () => void;
  notLoading: () => void;
}

export const JobCard = ({ vacancy, thisLoading, notLoading }: JobCardProps) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [vacancyId, setVacancyId] = useState<string | undefined>();
  const [openDialogPostulate, setOpenDialogPostulate] = useState(false);
  const { mutate: sendMessage } = useSendVacancyMessage();

  const onClickOpenDialog = () => setOpenDialog(true);
  const onClickCloseDialog = () => setOpenDialog(false);
  const onChangeMessage = (e: { target: { value: string } }) => setMessage(e.target.value);

  const onClickOpenDialogPostulate = (id: string) => {
    setVacancyId(id);
    setOpenDialogPostulate(true);
  };
  const onClickCloseDialogPostulate = () => setOpenDialogPostulate(false);

  const onSendMessage = () => {
    onClickCloseDialog();
    sendMessage(
      { vacancyId: vacancy.id as string, message },
      {
        onSuccess: () => {
          Swal.fire(t("messageSent"), t("successfully"), "success").then();
        },
        onError: () => {},
      }
    );
  };

  const getPriority = (priority?: string) => {
    const labelKey = PRIORITY_LABEL_KEYS[priority as keyof typeof PRIORITY_LABEL_KEYS];
    return labelKey ? t(labelKey) : "";
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <CardHeader title={vacancy.name} />
      <CardContent>
        <Box
          dangerouslySetInnerHTML={{ __html: vacancy.description ?? "" }}
          sx={{
            fontSize: "15px",
            mb: 2.5,
            lineHeight: 1.6,
            color: "text.primary",
            "& p": {
              mb: 2,
            },
            "& ul, & ol": {
              ml: 3,
              mb: 2,
            },
            "& li": {
              mb: 0.5,
            },
            "& h1, & h2, & h3, & h4": {
              mt: 3,
              mb: 2,
              fontWeight: 600,
              color: "text.primary",
            },
            "& a": {
              color: "primary.main",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            },
          }}
        />
        <Typography {...({ paragraph: true } as object)} style={{ fontSize: "15px" }} sx={{ display: "flex" }}>
          <b>{t("openingDate")}: </b>
          <span style={{ color: "white" }}> </span>
          <span style={{ color: "red" }}>
            {" " + format(parseISO(vacancy.createdAt ?? ""), "dd-MM-yyyy")}
          </span>
        </Typography>
        {vacancy.priority != null && vacancy.priority.length > 0 && (
          <Typography {...({ paragraph: true } as object)} style={{ fontSize: "15px" }}>
            <b>{t("priority")}: </b>
            {getPriority(vacancy.priority)}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "black", color: "white", marginRight: 10 }}
            onClick={onClickOpenDialog}
          >
            {t("vacancyInquiry")}
          </Button>
          <Link to={"/gestion-de-vacantes/vacantes/" + vacancy.id}>
            <Button
              variant="contained"
              style={{ backgroundColor: "black", color: "white", marginRight: 10 }}
            >
              {t("moreDetails")}
            </Button>
          </Link>
          <Button
            variant="contained"
            style={{ backgroundColor: "black", color: "white" }}
            onClick={() => {
              onClickOpenDialogPostulate(vacancy.id as string);
            }}
          >
            {t("nominateCandidate")}
          </Button>
        </Box>
        <DialogSupportComponent
          open={openDialog}
          title={titleSupportVacancy}
          content={contentSupportVacancy}
          onClickClose={onClickCloseDialog}
          message={message}
          onChangeMessage={onChangeMessage as never}
          onSend={onSendMessage}
        />
        <PostulateCandidateDialog
          handleClose={onClickCloseDialogPostulate}
          open={openDialogPostulate}
          vacancyId={vacancyId}
          vacancyTitle={vacancy.name}
          notLoading={notLoading}
          loading={thisLoading}
          t={t}
        />
      </CardContent>
    </Card>
  );
};
